import React from 'react';

import isCreateAnalysisEnabled from 'analysis-tool/isCreateAnalysisEnabled';
import {deprecatedTrackEvent} from 'analytics/Analytics';
import {InsightsFeature} from 'auth/capabilities/CapabilityFeatureConstants';
import {isFeatureEnabled} from 'auth/feature-enablement/FeatureStatus';
import {ActionOrigin, INSIGHTS_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import * as NavConstants from 'constants/NavConstants';
import {SearchSection} from 'graphql-client/__generated/graphqlGlobalTypes';
import {INSIGHTS_PRODUCT} from 'insights/NamingConstants';
import {CrumbLink, SplitBreadcrumbs} from 'nav/Breadcrumbs';
import ItemListing from 'nav/sidepanel/listing/ItemListing';
import {getNavigationURL} from 'nav/sidepanel/listing/SearchURLs';
import {SavedSearch} from 'search/saved-searches/SavedSearchUtil';
import {FilterKey, SearchParams, SearchType, SortKey} from 'search/SearchConstants';
import * as SearchUtil from 'search/SearchUtil';
import withSearching, {NavigationUpdate, SearchProps} from 'search/withSearching';
import useOnMount from 'ui-library/hooks/useOnMount';

import CreateAnalyticsDashboardButton from './CreateAnalyticsDashboardButton';
import {getDefaultAnalyticsSearchParams} from './getDefaultAnalyticsSearchParams';
import includeAnalysesInSearch from './includeAnalysesInSearch';

export interface AnalyticsSidepanelListingProps {
  activeItemId: string | null;
  isCompactDisplay: boolean;
  setIsCompactDisplay: (isCompact: boolean) => void;
  isExpanded: boolean;
}

function ListingCrumbs(props: {
  activeSavedSearch?: SavedSearch;
  onSelectSavedSearch: (savedSearch?: SavedSearch) => Promise<void>;
  onSearch: (searchParams: SearchParams) => void;
  searchParams: SearchParams;
  settingsComponents?: JSX.Element;
}): JSX.Element {
  if (props.activeSavedSearch) {
    return (
      <SplitBreadcrumbs
        crumbs={[
          <CrumbLink
            key="insights"
            text={INSIGHTS_PRODUCT}
            onClick={() => {
              props.onSearch(
                SearchUtil.clearSearchParams(props.searchParams, {
                  preservedFilterKeys: [],
                  filtersToReset: getDefaultAnalyticsSearchParams().filters,
                })
              );
              props.onSelectSavedSearch(undefined);
            }}
          />,
          <CrumbLink key="root" text={props.activeSavedSearch.name} />,
        ]}
        settingsIcon={props.settingsComponents}
      />
    );
  }
  return (
    <SplitBreadcrumbs
      crumbs={[<span key="root">{INSIGHTS_PRODUCT}</span>]}
      settingsIcon={props.settingsComponents}
    />
  );
}

export function AnalyticsSidepanelListing(props: AnalyticsSidepanelListingProps & SearchProps): JSX.Element {
  useOnMount(() => {
    deprecatedTrackEvent(INSIGHTS_ANALYTICS_ACTIONS.OPENED_INSIGHTS_MODULE);
  });

  return (
    <div className="sidepanel">
      <ItemListing
        {...props}
        actionSource={ActionOrigin.INSIGHTS_SIDEPANEL}
        savedSearchSection={SearchSection.ANALYTICS}
        additionalHeaderButtons={
          isFeatureEnabled({feature: InsightsFeature.INSIGHTS_CREATE}) || isCreateAnalysisEnabled() ? (
            <CreateAnalyticsDashboardButton className="u-mini-spacing-left" />
          ) : null
        }
        availableSortKeys={[SortKey.SORT_NAME_KEY, SortKey.SORT_MODIFIED_AT_KEY, SortKey.SORT_CREATED_AT_KEY]}
        availableTypes={
          includeAnalysesInSearch()
            ? [ModelTypeConstants.ANALYTICS_DASHBOARD, ModelTypeConstants.ANALYSIS]
            : [ModelTypeConstants.ANALYTICS_DASHBOARD]
        }
        // eslint-disable-next-line react/no-unstable-nested-components -- Disabling for now, but worth fixing
        makeCrumbs={(settingsComponents?: JSX.Element) => (
          <ListingCrumbs
            activeSavedSearch={props.activeSavedSearch}
            onSelectSavedSearch={props.onSelectSavedSearch}
            onSearch={props.onSearch}
            searchParams={props.searchParams}
            settingsComponents={settingsComponents}
          />
        )}
        extraPrimaryFilterOptions={[{filterKey: FilterKey.ANCESTOR_FOLDER, extraProps: {onlyProjects: true}}]}
        preservedFilterKeys={[]}
        section={NavConstants.Section.ANALYTICS}
        dataTestAttrs={{component: 'AnalyticsSidepanelListing', element: 'root'}}
      />
    </div>
  );
}

function getAnalyticsSearchURL(
  prevProps: AnalyticsSidepanelListingProps & SearchProps,
  props: AnalyticsSidepanelListingProps & SearchProps
): NavigationUpdate | null {
  if (!props.isExpanded) {
    return null;
  }
  return {
    url: getNavigationURL('/analytics', props.searchParams, props.filterErrors),
    options: {replace: prevProps.isExpanded === props.isExpanded},
  };
}

export default withSearching(
  AnalyticsSidepanelListing,
  SearchType.ANALYSES_AND_ANALYTICS_DASHBOARDS,
  NavConstants.SECTIONS.ANALYTICS,
  {
    defaultSearchParams: getDefaultAnalyticsSearchParams(),
    getNavigationUpdate: getAnalyticsSearchURL,
    filterKeysToRemember: [FilterKey.TYPES],
  }
);
